"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useCurrentAccountId } from "@zapier/identity";
import { v4 as generateUuid } from "uuid";
import {
  emitZapCreationSuccessEvent,
  emitZapEntryPointEvent,
  ENTRY_POINT_LOCATION,
  ENTRY_POINT_METHOD,
} from "src/observability/tracking";
import { LOCALSTORAGE_TEMPLATE_CREATE_KEY } from "src/constants";
import { useLocalStorage } from "./useLocalStorage";

const ALERT_DURATION = 10_000;

export const COPILOT_QUERY_PARAM = "copilot_prompt";
export const COPILOT_PROMPT = "Help finish setting up my Zap";

export function useCreateTemplate({
  shouldCreateAttemptId = false,
  shouldRedirect,
}: {
  shouldCreateAttemptId: boolean;
  shouldRedirect?: boolean;
}) {
  const createdTemplate = useRef(null);
  const currentAccountId = useCurrentAccountId();

  // used only for Zap Templates
  const [zapTemplateAttemptId, setZapTemplateAttemptId] = useState<string>();
  const [error, setError] = useState<string | null>(null);
  const [creationScreen, setCreationScreen] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [templateToCreate, setTemplateToCreate] = useState<string | null>(null);

  const {
    setValue: setLocalStorageTemplateCreateKey,
    storedValue: localTemplateKey,
    removeValue: removeLocalStorageTemplateCreateKey,
  } = useLocalStorage<string>(LOCALSTORAGE_TEMPLATE_CREATE_KEY);

  const createTemplate = useCallback(
    (templateKey: string) => {
      setZapTemplateAttemptId(
        shouldCreateAttemptId ? generateUuid() : undefined,
      );
      setError(null);
      setCreationScreen(currentAccountId ? templateKey : null);
      setTemplateToCreate(templateKey);
      if (!currentAccountId) {
        // Set the template we want to create in localStorage so it will kick off
        // execution when they land back on this page.
        setLocalStorageTemplateCreateKey(templateKey);

        handleSignupRedirect();
      }
    },
    [setTemplateToCreate],
  );
  const handleSetError = useCallback(
    (error: string | null) => {
      setError(error);
    },
    [setError],
  );

  /**
   * This useEffect checks whether we've indicated that we want to create a template,
   * and if other requirements are met (the user is logged in), it will execute the template.
   *
   * It also handles the logged out state where we set the localStorage key and send
   * them through the sign up / log in flow to be redirected back here.
   */
  useEffect(() => {
    const url = new URL(window.location.href);
    const createQueryParam = url.searchParams.get("create") === "true";

    /**
     * If the user tries to create a template while logged out, we save that template in
     * localStorage, and upon arriving back here from the login/signup flow, we will
     * check for it and initiate the creation if the create=true query param is set.
     */

    const templateKey = templateToCreate ?? localTemplateKey;

    if (!templateKey) return;
    if (createQueryParam) {
      setCreationScreen(templateKey);
    }

    const shouldCreateTemplate =
      !isCreating && (creationScreen || createQueryParam);

    if (shouldCreateTemplate && !currentAccountId) {
      return handleSignupRedirect();
    }

    if (!isCreating && shouldCreateTemplate && currentAccountId) {
      removeLocalStorageTemplateCreateKey();

      if (zapTemplateAttemptId) {
        emitZapEntryPointEvent({
          zap_creation_attempt_id: zapTemplateAttemptId,
          entry_point_method_name: ENTRY_POINT_METHOD,
          entry_point_location_name: ENTRY_POINT_LOCATION,
          copilot_prompt: COPILOT_PROMPT,
        });
      }

      executeTemplate({
        templateKey,
        url: window.location.href,
        attemptId: zapTemplateAttemptId,
        currentAccountId,
      })
        .then(() => {
          setIsCreating(false);
          setCreationScreen(null);
        })
        .catch((error) => {
          handleSetError(handleErrorMessage(error));
          setTimeout(() => handleSetError(null), ALERT_DURATION);
          // Only set this to false if something goes wrong, because `handleCreate`
          // will redirect them after the template is executed otherwise.
          setIsCreating(false);
          setCreationScreen(null);
        });

      setIsCreating(true);
      setTemplateToCreate(null);
    }
  }, [
    currentAccountId,
    setTemplateToCreate,
    templateToCreate,
    isCreating,
    setIsCreating,
    creationScreen,
    setCreationScreen,
    createTemplate,
    shouldRedirect,
    handleSetError,
    localTemplateKey,
  ]);

  return {
    createTemplate,
    creationScreen,
    createdTemplate: createdTemplate.current,
    error,
    setError: handleSetError,
  };
}

async function executeTemplate({
  templateKey,
  url,
  currentAccountId,
  attemptId,
}: {
  templateKey: string;
  url: string;
  currentAccountId: number;
  attemptId?: string;
}) {
  const resp = await fetch("/api/templates/execute", {
    method: "POST",
    body: JSON.stringify({ templateSlug: templateKey, url }),
  });

  const body = await resp.json();
  if (!resp.ok) {
    const status = resp.status;
    const error = body.errors[0];
    if (error.code === "service:unknown") {
      throw new Error(`Failed to execute (status: ${status}) ${error.error}`);
    }
    throw error;
  }

  const redirectTo = new URL(body.redirectTo, window.location.origin);

  if (attemptId) {
    emitZapCreationSuccessEvent({
      zap_creation_attempt_id: attemptId,
      zap_id: body.redirectTo.split("/").pop(),
      account_id: currentAccountId,
    });
    redirectTo.searchParams.set("attempt_id", attemptId);
    redirectTo.searchParams.set(COPILOT_QUERY_PARAM, COPILOT_PROMPT);
  }

  window.location.href = redirectTo.href;
}

/**
 * If the user is not logged in, we need to send the through login first.
 *
 */
const handleSignupRedirect = () => {
  const url = new URL(window.location.href);
  url.searchParams.set("create", "true");

  return window.location.assign(
    "/sign-up" +
      `?next=${encodeURIComponent(`${url.pathname}?${url.searchParams.toString()}`)}`,
  );
};

const DEFAULT_ERROR_MESSAGE =
  "Something went wrong while trying to use this template.";
function handleErrorMessage(error: {
  code: string;
  status: string;
  title: string;
  detail: string;
}) {
  switch (error.code) {
    case "tables:limits:too_many_tables":
    case "limits:too_many_tables": {
      return `You have reached the limit of Tables for your plan. Please upgrade your plan to create more.`;
    }
    case "limits:projects":
    case "interfaces:limits:projects": {
      return `You have reached the limit of Interfaces for your plan. Please upgrade your plan to create more.`;
    }
    case "limits:chatbots":
    case "chatbots:limits:chatbots": {
      return `You have reached the limit of Chatbots for your plan. Please upgrade your plan to create more.`;
    }
    case "upstream-error": {
      if (
        error.title === "GulliverApiError" &&
        (error.status === "426" || error.status === "402")
      ) {
        return "This template contains Zap(s) that includes features (steps, paths, premium apps) not covered by your plan. Please upgrade your plan to use this template.";
      }
      return DEFAULT_ERROR_MESSAGE;
    }

    default: {
      return DEFAULT_ERROR_MESSAGE;
    }
  }
}
